import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { cs } from "@/i18n/cs";

interface PaletteItem {
  id: string;
  label: string;
  kind: string;
  run: () => void;
}

let openPaletteExternal: (() => void) | null = null;
/** Otevře paletu odkudkoliv (tlačítko ⌘K v top baru). */
export function openPalette() {
  openPaletteExternal?.();
}

/** ⌘K / Ctrl+K paleta: navigace + projekty (dle /Design, sekce PALETTE). */
export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  const projects = useQuery({
    queryKey: ["projects"],
    queryFn: api.projects,
    enabled: open,
  });

  useEffect(() => {
    openPaletteExternal = () => {
      setOpen(true);
      setQuery("");
      setSelected(0);
    };
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
        setQuery("");
        setSelected(0);
      } else if (e.key === "Escape") {
        setOpen(false);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      openPaletteExternal = null;
    };
  }, []);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  const items = useMemo<PaletteItem[]>(() => {
    const nav: PaletteItem[] = [
      { id: "nav-command", label: "Command Center", kind: cs.palette.navigate, run: () => void navigate({ to: "/" }) },
      { id: "nav-projects", label: "Projekty", kind: cs.palette.navigate, run: () => void navigate({ to: "/projects" }) },
      { id: "nav-decisions", label: "Rozhodnutí", kind: cs.palette.navigate, run: () => void navigate({ to: "/decisions" }) },
      { id: "nav-human-tasks", label: "Úkoly pro člověka", kind: cs.palette.navigate, run: () => void navigate({ to: "/human-tasks" }) },
      { id: "nav-blockers", label: "Blokace", kind: cs.palette.navigate, run: () => void navigate({ to: "/blockers" }) },
      { id: "nav-proposals", label: "Návrhy AI", kind: cs.palette.navigate, run: () => void navigate({ to: "/proposals" }) },
      { id: "nav-audit", label: "Activity / Audit", kind: cs.palette.navigate, run: () => void navigate({ to: "/audit" }) },
      { id: "nav-settings", label: "Nastavení", kind: cs.palette.navigate, run: () => void navigate({ to: "/settings" }) },
    ];
    const projectItems: PaletteItem[] = (projects.data?.projects ?? []).map((p) => ({
      id: `project-${p.id}`,
      label: `Otevřít projekt: ${p.name}`,
      kind: cs.palette.project,
      run: () => void navigate({ to: "/projects/$slug", params: { slug: p.slug } }),
    }));
    const all = [...projectItems, ...nav];
    const q = query.trim().toLowerCase();
    if (!q) return all;
    return all.filter((i) => i.label.toLowerCase().includes(q));
  }, [projects.data, query, navigate]);

  if (!open) return null;

  const run = (item: PaletteItem | undefined) => {
    if (!item) return;
    setOpen(false);
    item.run();
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center pt-[18vh]"
      style={{ background: "var(--scrim)" }}
      onClick={() => setOpen(false)}
      data-testid="palette"
    >
      <div
        className="af-animate-fade w-[600px] max-w-[calc(100vw-24px)]"
        // plovoucí plocha musí být neprůhledná — `--surface-inset` je jen tenký
        // závoj a stránka pod paletou prosvítala až do nečitelnosti
        style={{
          borderRadius: "var(--radius-panel)",
          border: "1px solid var(--accent-border)",
          background: "var(--surface-overlay)",
          backdropFilter: "var(--surface-blur)",
          boxShadow: "var(--emphasis-accent)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="flex items-center gap-3 px-4 py-3"
          style={{ borderBottom: "1px solid var(--border-subtle)" }}
        >
          <span style={{ color: "var(--accent)" }}>›</span>
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setSelected(0);
            }}
            onKeyDown={(e) => {
              if (e.key === "ArrowDown") {
                e.preventDefault();
                setSelected((s) => Math.min(items.length - 1, s + 1));
              } else if (e.key === "ArrowUp") {
                e.preventDefault();
                setSelected((s) => Math.max(0, s - 1));
              } else if (e.key === "Enter") {
                run(items[selected]);
              }
            }}
            placeholder={cs.palette.placeholder}
            className="w-full bg-transparent text-[12px] outline-none"
            style={{ color: "var(--text-primary)" }}
          />
          <span className="af-label-dim border px-1.5 py-0.5" style={{ borderColor: "var(--border-default)" }}>
            ESC
          </span>
        </div>
        <div className="max-h-[320px] overflow-y-auto py-1">
          {items.length === 0 ? (
            <div className="px-4 py-4 text-[12px]" style={{ color: "var(--text-tertiary)" }}>
              {cs.palette.empty}
            </div>
          ) : (
            items.map((item, i) => (
              <button
                key={item.id}
                type="button"
                className="flex w-full cursor-pointer items-center justify-between px-4 py-2.5 text-left"
                style={{ background: i === selected ? "color-mix(in srgb, var(--accent) 7%, transparent)" : "transparent" }}
                onMouseEnter={() => setSelected(i)}
                onClick={() => run(item)}
              >
                <span className="text-[11px]" style={{ color: "var(--text-primary)" }}>
                  {item.label}
                </span>
                <span className="af-label-dim">{item.kind}</span>
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
